// Home-screen install support (D-007).
//
// Two very different platforms:
//
//  - Android/Chrome fires `beforeinstallprompt`, which can be stashed and
//    replayed from our own button — a genuine one-tap install. The event only
//    fires when the app is installable (manifest with a fetchable >=192px
//    icon, a registered service worker with a fetch handler, HTTPS).
//  - iOS has no programmatic equivalent. Apple does not expose one, so the
//    only honest option is to show the Share -> Add to Home Screen steps.
//
// The capture listener is installed at module scope because
// `beforeinstallprompt` can fire before any component mounts; a component that
// only subscribes on mount would miss it.

/** Minimal shape of the non-standard beforeinstallprompt event. */
export interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type Platform = 'ios' | 'android' | 'macos' | 'other';

/**
 * iPadOS 13+ reports a desktop Safari user agent, so touch points are the only
 * reliable tell for an iPad.
 */
export function isIOS(
  ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  maxTouchPoints: number = typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints || 0
): boolean {
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && maxTouchPoints > 1;
}

export function detectPlatform(
  ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  maxTouchPoints: number = typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints || 0
): Platform {
  if (isIOS(ua, maxTouchPoints)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Macintosh|Mac OS X/.test(ua)) return 'macos';
  return 'other';
}

/**
 * Already installed / launched from the home screen.
 *
 * `display-mode: standalone` covers Android and modern iOS; navigator.standalone
 * is the legacy iOS Safari flag.
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  } catch {
    // matchMedia can throw in some embedded webviews.
  }
  return (window.navigator as unknown as { standalone?: boolean })?.standalone === true;
}

let deferredPrompt: InstallPromptEvent | null = null;
const listeners = new Set<(available: boolean) => void>();

function notify(): void {
  listeners.forEach((listener) => listener(deferredPrompt !== null));
}

/** Whether a native install prompt is currently available to replay. */
export function isInstallPromptAvailable(): boolean {
  return deferredPrompt !== null;
}

/** For tests: forget any captured prompt. */
export function resetInstallPrompt(): void {
  deferredPrompt = null;
  notify();
}

function handleBeforeInstallPrompt(event: Event): void {
  // Suppress Chrome's own mini-infobar so our button is the single entry point.
  event.preventDefault();
  deferredPrompt = event as InstallPromptEvent;
  notify();
}

function handleAppInstalled(): void {
  // A prompt can only be used once, and it is moot after installation.
  deferredPrompt = null;
  notify();
}

/**
 * Start listening for install-prompt availability. Idempotent, and safe to
 * call before React mounts.
 */
export function initInstallPromptCapture(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  window.addEventListener('appinstalled', handleAppInstalled);
}

/** Subscribe to availability changes; returns an unsubscribe function. */
export function subscribeInstallPrompt(listener: (available: boolean) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Replay the captured prompt. Resolves with the user's choice, or
 * 'unavailable' when there is nothing to replay (iOS, already installed, or
 * the app does not currently meet the installability bar).
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferredPrompt;
  if (!event) return 'unavailable';
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  } catch {
    return 'unavailable';
  } finally {
    // Chrome allows a stashed prompt to be used only once.
    deferredPrompt = null;
    notify();
  }
}

// Begin capturing as soon as this module is imported.
initInstallPromptCapture();
